import type { ProjectResource, TenantProjectVariablesResource, TenantVariableResource } from "@octopusdeploy/octopus-server-client";
import ProjectTemplatesInlineContent from "~/areas/projects/components/Variables/TenantVariables/ProjectTemplatesInlineContent";
import ScopedProjectTemplatesInlineContent from "~/areas/projects/components/Variables/TenantVariables/ScopedTenantVariables/ScopedProjectTemplatesInlineContent";
import type { ChangedProjectTemplatesScope, ScopedProjectTemplatesScopeChangeTracker } from "~/areas/projects/components/Variables/TenantVariables/ScopedTenantVariables/useScopedProjectTemplatesScopeChangeTracker";
import { useScopedProjectTemplatesScopeChangeTracker } from "~/areas/projects/components/Variables/TenantVariables/ScopedTenantVariables/useScopedProjectTemplatesScopeChangeTracker";
import { useScopedProjectTemplatesState } from "~/areas/projects/components/Variables/TenantVariables/ScopedTenantVariables/useScopedProjectTemplatesState";
import type { ScopedProjectTemplatesState } from "~/areas/projects/components/Variables/TenantVariables/ScopedTenantVariables/useScopedProjectTemplatesState";
import type { TenantVariablesPageSaveAction } from "~/areas/projects/components/Variables/TenantVariables/SharedTenantVariablesPageContent";
import ProjectTenantVariablesPageContent from "~/areas/projects/components/Variables/TenantVariables/SharedTenantVariablesPageContent";
import { useProjectTemplatesState } from "~/areas/projects/components/Variables/TenantVariables/useProjectTemplatesState";
import type { ProjectTemplatesState } from "~/areas/projects/components/Variables/TenantVariables/useProjectTemplatesState";
import type { ChangedProjectTemplateVariableValue, ProjectTenantVariableValueChangeTracker } from "~/areas/projects/components/Variables/TenantVariables/useProjectTenantVariableValueChangeTracker";
import { useProjectTenantVariableValueChangeTracker } from "~/areas/projects/components/Variables/TenantVariables/useProjectTenantVariableValueChangeTracker";
import { useTenantVariableFiltersData } from "~/areas/projects/components/Variables/TenantVariables/useTenantVariableFiltersData";
import { useTenantVariableResourceOptions } from "~/areas/projects/components/Variables/TenantVariables/useTenantVariableResourceOptions";
import { repository } from "~/clientInstance";
import { type DoBusyTask } from "~/components/DataBaseComponent/index";
import { useLegacyDoBusyTask } from "~/components/DataBaseComponent/useLegacyDoBusyTask";
import { isFeatureToggleEnabled } from "~/components/FeatureToggle/New/FeatureToggleContext";
interface TenantVariablesProjectTemplatesPageProps {
    project: ProjectResource;
}
export default function TenantVariablesProjectTemplatesPage({ project }: TenantVariablesProjectTemplatesPageProps) {
    return isFeatureToggleEnabled("CommonVariableScopingFeatureToggle") ? <ScopedProjectTemplatesPage project={project}/> : <UnscopedProjectTemplatesPage project={project}/>;
}
function ScopedProjectTemplatesPage({ project }: TenantVariablesProjectTemplatesPageProps) {
    const { status: legacyStatus, doBusyTask } = useLegacyDoBusyTask();
    const scopedContentState = useScopedProjectTemplatesState(project, doBusyTask);
    const filterData = useTenantVariableFiltersData(project.Id, doBusyTask);
    const variableResourceOptions = useTenantVariableResourceOptions(doBusyTask);
    const valueChangeTracker = useProjectTenantVariableValueChangeTracker();
    const scopeChangeTracker = useScopedProjectTemplatesScopeChangeTracker();
    const saveAction: TenantVariablesPageSaveAction = {
        onClick: async () => {
            await doBusyTask(async () => {
                await saveScoped(valueChangeTracker, scopeChangeTracker, scopedContentState);
            });
        },
        disabled: !valueChangeTracker.hasAny() && !scopeChangeTracker.hasAny(),
    };
    return (<ProjectTenantVariablesPageContent legacyStatus={legacyStatus} project={project} doBusyTask={doBusyTask} saveAction={saveAction} page={"project"} warnings={scopedContentState.warnings}>
            {project ? (<ScopedProjectTemplatesInlineContent project={project} connectedEnvironments={scopedContentState.connectedEnvironments} doBusyTask={doBusyTask} contentState={scopedContentState} filterData={filterData} variableResourceOptions={variableResourceOptions} variableValueTracker={valueChangeTracker} scopeChangeTracker={scopeChangeTracker}/>) : null}
        </ProjectTenantVariablesPageContent>);
}
function UnscopedProjectTemplatesPage({ project }: TenantVariablesProjectTemplatesPageProps) {
    const { status: legacyStatus, doBusyTask } = useLegacyDoBusyTask();
    const contentState = useProjectTemplatesState(project, doBusyTask);
    const filterData = useTenantVariableFiltersData(project.Id, doBusyTask);
    const variableResourceOptions = useTenantVariableResourceOptions(doBusyTask);
    const valueChangeTracker = useProjectTenantVariableValueChangeTracker();
    const saveAction: TenantVariablesPageSaveAction = {
        onClick: async () => {
            await doBusyTask(async () => {
                await save(valueChangeTracker, contentState);
            });
        },
        disabled: !valueChangeTracker.hasAny(),
    };
    return (<ProjectTenantVariablesPageContent legacyStatus={legacyStatus} project={project} doBusyTask={doBusyTask} saveAction={saveAction} page={"project"} warnings={contentState.warnings}>
            {project ? <ProjectTemplatesInlineContent project={project} doBusyTask={doBusyTask} contentState={contentState} filterData={filterData} variableResourceOptions={variableResourceOptions} variableValueTracker={valueChangeTracker}/> : null}
        </ProjectTenantVariablesPageContent>);
}
async function save(valueTracker: ProjectTenantVariableValueChangeTracker, contentState: ProjectTemplatesState) {
    await saveProjectTemplateVariableValues(valueTracker);
    await contentState.actions.onSaveProjectTemplates();
    valueTracker.reset();
}
async function saveScoped(valueTracker: ProjectTenantVariableValueChangeTracker, scopeTracker: ScopedProjectTemplatesScopeChangeTracker, scopedContentState: ScopedProjectTemplatesState) {
    await saveProjectTemplates(valueTracker, scopeTracker);
    await scopedContentState.actions.onSave();
    valueTracker.reset();
    scopeTracker.reset();
}
async function saveProjectTemplateVariableValues(valueTracker: ProjectTenantVariableValueChangeTracker) {
    const changedTenants = valueTracker.changedTenants();
    const tenantVariablesPromises = Array.from(changedTenants).map((id) => repository.Tenants.getVariablesByTenantId(id));
    const tenantVariables = await Promise.all(tenantVariablesPromises);
    const tenantVariablesLookup: {
        [tenantId: string]: TenantVariableResource;
    } = {};
    for (const tenantVariable of tenantVariables) {
        tenantVariablesLookup[tenantVariable.TenantId] = tenantVariable;
    }
    const modifiedTenantVariables = modifyTenantVariables(tenantVariablesLookup, valueTracker.values());
    const savingPromises = modifiedTenantVariables.map((variables) => repository.Tenants.setVariablesByTenantId(variables.TenantId, variables));
    await Promise.all(savingPromises);
}
async function saveProjectTemplates(valueTracker: ProjectTenantVariableValueChangeTracker, scopeTracker: ScopedProjectTemplatesScopeChangeTracker) {
    const changedTenants = new Set([...valueTracker.changedTenants(), ...scopeTracker.changedTenants()]);
    const tenantVariablesPromises = Array.from(changedTenants).map((id) => repository.Tenants.getProjectVariablesByTenantId(id));
    const tenantVariablesResponses = await Promise.all(tenantVariablesPromises);
    const tenantVariablesLookup: {
        [tenantId: string]: TenantProjectVariablesResource;
    } = {};
    for (const response of tenantVariablesResponses) {
        const { TenantId, Variables } = response;
        tenantVariablesLookup[TenantId] = { TenantId, Variables: Variables };
    }
    const modifiedTenantVariables = modifyProjectTenantVariables(tenantVariablesLookup, valueTracker.values(), scopeTracker.values());
    const savingPromises = modifiedTenantVariables.map((variables) => repository.Tenants.setProjectVariablesByTenantId(variables.TenantId, variables));
    await Promise.all(savingPromises);
}
async function saveScopedTenantVariablesProjectTemplatesValues(doBusyTask: DoBusyTask, valueTracker: ProjectTenantVariableValueChangeTracker) {
    return doBusyTask(async () => {
        const changedTenants = valueTracker.changedTenants();
        const tenantVariablesPromises = Array.from(changedTenants).map((id) => repository.Tenants.getProjectVariablesByTenantId(id));
        const tenantVariablesResponses = await Promise.all(tenantVariablesPromises);
        const tenantVariablesLookup: {
            [tenantId: string]: TenantProjectVariablesResource;
        } = {};
        for (const response of tenantVariablesResponses) {
            const { TenantId, Variables } = response;
            tenantVariablesLookup[TenantId] = { TenantId, Variables: Variables };
        }
        const modifiedTenantVariables = modifyScopedTenantVariables(tenantVariablesLookup, valueTracker.values());
        const savingPromises = modifiedTenantVariables.map((variables) => repository.Tenants.setProjectVariablesByTenantId(variables.TenantId, variables));
        await Promise.all(savingPromises);
    });
}
function modifyProjectTenantVariables(tenantVariables: {
    [tenantId: string]: TenantProjectVariablesResource;
}, valueChanges: ChangedProjectTemplateVariableValue[], scopeChanges: ChangedProjectTemplatesScope[]): TenantProjectVariablesResource[] {
    const modified = new Map<string, TenantProjectVariablesResource>();
    for (const { id, projectId, templateId, tenantId, value, status } of valueChanges) {
        const variables = tenantVariables[tenantId];
        if (status === "deleted") {
            variables.Variables = variables.Variables.filter((v) => v.Id !== id);
            modified.set(tenantId, variables);
            continue;
        }
        const variable = variables.Variables.find((v) => (id && v.Id === id) || (!id && matchProjectVariableByTemplate(v, projectId, templateId)));
        if (!variable) {
            const scope = scopeChanges.find((s) => (id && s.guid === id) || (!id && matchProjectScopeChangeByTemplate(s, tenantId, projectId, templateId)));
            variables.Variables.push({
                Id: id,
                ProjectId: projectId,
                TemplateId: templateId,
                Value: value ?? "",
                Template: undefined,
                Scope: { EnvironmentIds: scope?.environmentIds ?? [] },
            });
            modified.set(tenantId, variables);
            continue;
        }
        variable.Value = value ?? "";
        modified.set(tenantId, variables);
    }
    for (const { guid, projectId, templateId, tenantId, environmentIds, status } of scopeChanges) {
        const variables = modified.get(tenantId) ?? tenantVariables[tenantId];
        if (status === "deleted") {
            variables.Variables = variables.Variables.filter((v) => v.Id !== guid);
            modified.set(tenantId, variables);
            continue;
        }
        const variable = variables.Variables.find((v) => (guid && v.Id === guid) || (!guid && matchProjectVariableByTemplate(v, projectId, templateId)));
        if (!variable) {
            const valueChange = valueChanges.find((s) => matchScopedProjectValueChangeByTemplate(s, tenantId, projectId, templateId));
            variables.Variables.push({
                Id: guid,
                ProjectId: projectId,
                TemplateId: templateId,
                Value: valueChange?.value ?? "",
                Template: undefined,
                Scope: { EnvironmentIds: environmentIds ?? [] },
            });
            modified.set(tenantId, variables);
            continue;
        }
        variable.Scope.EnvironmentIds = environmentIds ?? [];
        modified.set(tenantId, variables);
    }
    return Array.from(modified.values());
}
function matchProjectVariableByTemplate(variable: {
    Id?: string;
    ProjectId: string;
    TemplateId: string;
}, projectId: string, templateId: string): boolean {
    return !variable.Id && variable.ProjectId === projectId && variable.TemplateId === templateId;
}
function matchProjectScopeChangeByTemplate(changedScope: ChangedProjectTemplatesScope, tenantId: string, projectId: string, templateId: string): boolean {
    return !changedScope.guid && changedScope.projectId === projectId && changedScope.templateId === templateId && changedScope.tenantId === tenantId;
}
function matchScopedProjectValueChangeByTemplate(changedValue: ChangedProjectTemplateVariableValue, tenantId: string, projectId: string, templateId: string): boolean {
    return changedValue.projectId === projectId && changedValue.templateId === templateId && changedValue.tenantId === tenantId;
}
function modifyTenantVariables(tenantVariables: {
    [tenantId: string]: TenantVariableResource;
}, changes: ChangedProjectTemplateVariableValue[]): TenantVariableResource[] {
    const modified = new Map<string, TenantVariableResource>();
    for (const { projectId, templateId, tenantId, environmentId, value } of changes) {
        const variables = tenantVariables[tenantId];
        variables.ProjectVariables[projectId].Variables[environmentId][templateId] = value ?? "";
        modified.set(tenantId, variables);
    }
    return Array.from(modified.values());
}
function modifyScopedTenantVariables(tenantVariables: {
    [tenantId: string]: TenantProjectVariablesResource;
}, changes: ChangedProjectTemplateVariableValue[]): TenantProjectVariablesResource[] {
    const modified = new Map<string, TenantProjectVariablesResource>();
    for (const { projectId, templateId, tenantId, environmentId, value } of changes) {
        const payload = tenantVariables[tenantId];
        const variable = payload.Variables.find((v) => v.ProjectId === projectId && v.TemplateId === templateId && v.Scope.EnvironmentIds.includes(environmentId));
        if (!variable) {
            payload.Variables.push({
                Id: "",
                ProjectId: projectId,
                TemplateId: templateId,
                Value: value ?? "",
                Template: undefined,
                Scope: { EnvironmentIds: [environmentId] },
            });
            modified.set(tenantId, payload);
            continue;
        }
        variable.Value = value ?? "";
        modified.set(tenantId, payload);
    }
    return Array.from(modified.values());
}
